import React from 'react';
import { Check, X } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { AiButton, Field, StreamPreview, inputCls } from '../shared';

export const validate = (wf) => {
  const s = wf.data?.seo || {};
  if (!s.seo_title?.trim()) return 'Add an SEO title.';
  if (!s.seo_description?.trim()) return 'Add an SEO description.';
  if (!s.slug?.trim()) return 'Add a URL slug.';
  return null;
};

const Counter = ({ value, max }) => (
  <span className={`text-[10px] ${value.length > max ? 'text-red-600' : 'text-ink-3'}`}>{value.length}/{max}</span>
);

const StepSeo = ({ wf, setData, setArticle, runAi, ai }) => {
  const d = wf.data?.seo || {};
  const running = ai.running && ai.action === 'seo';

  const optimise = async () => {
    const res = await runAi('seo');
    if (res) {
      setData('seo', { ai: res, seo_title: res.seo_title, seo_description: res.seo_description, slug: res.slug, tags: res.tags || [], excerpt: res.excerpt || '' });
      if (!wf.article?.excerpt?.trim() && res.excerpt) setArticle({ excerpt: res.excerpt });
    }
  };

  return (
    <div className="space-y-6">
      <AiButton onClick={optimise} running={running} label="Optimise with AI" runningLabel="Optimising…" testId="seo-generate-btn" />
      {running && <StreamPreview text={ai.text} label="Running on-page SEO…" />}
      <div className="grid md:grid-cols-2 gap-5">
        <Field label={<>SEO title <Counter value={d.seo_title || ''} max={60} /></>} required testId="seo-title-field">
          <Input value={d.seo_title || ''} onChange={(e) => setData('seo', { seo_title: e.target.value })} className={inputCls} data-testid="seo-title-input" />
        </Field>
        <Field label="URL slug" required hint={`/blog/${d.slug || '…'}`} testId="seo-slug-field">
          <Input value={d.slug || ''} onChange={(e) => setData('seo', { slug: e.target.value })} className={inputCls} data-testid="seo-slug-input" />
        </Field>
      </div>
      <Field label={<>Meta description <Counter value={d.seo_description || ''} max={160} /></>} required testId="seo-desc-field">
        <Textarea value={d.seo_description || ''} onChange={(e) => setData('seo', { seo_description: e.target.value })} rows={3} className={inputCls} data-testid="seo-desc-input" />
      </Field>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Tags" hint="Comma separated. Used as-is on the published post." testId="seo-tags-field">
          <Input value={(d.tags || []).join(', ')} onChange={(e) => setData('seo', { tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} className={inputCls} data-testid="seo-tags-input" />
        </Field>
        <Field label={<>Excerpt <Counter value={d.excerpt || ''} max={200} /></>} testId="seo-excerpt-field">
          <Input value={d.excerpt || ''} onChange={(e) => { setData('seo', { excerpt: e.target.value }); setArticle({ excerpt: e.target.value }); }} className={inputCls} />
        </Field>
      </div>
      {d.ai?.checks?.length > 0 && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[var(--line-2)] p-5" data-testid="seo-checks">
            <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-3">On-page checklist</div>
            <ul className="space-y-2">
              {d.ai.checks.map((c, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  {c.pass ? <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <X className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                  <div><span className="text-ink">{c.item}</span>{c.note && <div className="text-[11px] text-ink-3">{c.note}</div>}</div>
                </li>
              ))}
            </ul>
          </div>
          {d.ai.internal_links?.length > 0 && (
            <div className="rounded-2xl border border-[var(--line-2)] p-5">
              <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-3">Internal link opportunities</div>
              <ul className="space-y-2 text-sm">
                {d.ai.internal_links.map((l, i) => (
                  <li key={i}><span className="text-ink font-medium">{l.anchor}</span> → <span className="text-ink-2">{l.path}</span><div className="text-[11px] text-ink-3">{l.where}</div></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepSeo;
